import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Grid, Card, CardContent, Chip, Paper } from '@mui/material';
import { ArrowLeft, Edit, MapPin, Globe, Phone, Mail, School, Building2, Coins, Clock } from 'lucide-react';
import { useSuperAdminSchoolStore } from '@/store/superAdmin/schoolStore';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';



export function GlobalSchoolDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    selectedSchool: school, 
    schoolConfiguration: fullConfig,
    loading, 
    error, 
    fetchSchoolById, 
    fetchSchoolConfiguration
  } = useSuperAdminSchoolStore();
  const { organizations, fetchOrganizations } = useOrganizationStore();

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    if (id) {
      fetchSchoolById(id);
      fetchSchoolConfiguration(id);
    }
  }, [id, fetchSchoolById, fetchSchoolConfiguration]);

  if (loading) return <Loader message="Loading school details..." />;
  if (error) return <ErrorState message={error} onRetry={() => { fetchSchoolById(id); fetchSchoolConfiguration(id); }} />;
  if (!school) return <ErrorState message="School not found" onRetry={() => fetchSchoolById(id)} />;

  const org = organizations.find((o) => o.id === (school.organization_id || school.organizationId));
  const orgName = org ? org.organization_name || org.name : (school.organization_id || 'N/A');
  const isActive = school.status?.toUpperCase() === 'ACTIVE';

  // Parse nested configuration objects from API response
  const domain = fullConfig?.domain || school?.domain || {};
  const settings = fullConfig?.settings || school?.settings || school?.configuration || {};
  const website = fullConfig?.website || school?.website || {};

  const isConfigured = !!(domain?.id || settings?.id || website?.id || fullConfig?.domain?.id || fullConfig?.settings?.id);

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={school.school_name || school.name}
        subtitle="School Details & Configuration"
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/super-admin/schools')}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: tokens.colors.border,
                color: tokens.colors.text.primary,
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit size={18} />}
              onClick={() => navigate(`/super-admin/schools/edit/${school.id}`)}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              Edit Details
            </Button>
          </Box>
        }
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          {/* General Information Card */}
          <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <School size={20} /> General Information
                </Typography>
                <Chip
                  label={isActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    backgroundColor: isActive ? tokens.colors.success[50] : tokens.colors.error[50],
                    color: isActive ? tokens.colors.success.main : tokens.colors.error.main,
                    fontWeight: 600,
                    borderRadius: tokens.radius.sm,
                  }}
                />
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                    <Building2 size={18} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{orgName}</Typography>
                  </Box>
                  {school.school_code && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, bgcolor: tokens.colors.grey[100], px: 1, py: 0.25, borderRadius: tokens.radius.sm, color: tokens.colors.text.primary }}>
                        CODE: {school.school_code}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                    <Mail size={18} />
                    <Typography variant="body2">{school.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                    <Phone size={18} />
                    <Typography variant="body2">{school.phone}</Typography>
                  </Box>
                  {school.website && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary }}>
                      <Globe size={18} />
                      <Typography variant="body2" component="a" href={school.website} target="_blank" sx={{ color: tokens.colors.info.main, textDecoration: 'none' }}>
                        {school.website}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 2 }}>
                    <MapPin size={18} sx={{ mt: 0.5, flexShrink: 0 }} />
                    <Typography variant="body2">
                      {school.address}<br />
                      {school.city}, {school.state} {school.postal_code || school.postalCode}<br />
                      {school.country}
                    </Typography>
                  </Box>
                  {(settings.currency || school.currency) && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                      <Coins size={18} />
                      <Typography variant="body2">Currency: {settings.currency || school.currency}</Typography>
                    </Box>
                  )}
                  {(settings.timezone || school.timezone) && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1.5 }}>
                      <Clock size={18} />
                      <Typography variant="body2">Timezone: {settings.timezone || school.timezone}</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>


          {/* Assigned Modules Section */}
          <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2.5, color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                Assigned Modules ({school.school_modules?.length || 0})
              </Typography>
              {school.school_modules && school.school_modules.length > 0 ? (
                <Grid container spacing={1.5}>
                  {school.school_modules.map((sm) => {
                    const mod = sm.module || sm;
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={sm.id || sm.module_id}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: tokens.radius.sm,
                            border: `1px solid ${tokens.colors.border}`,
                            bgcolor: tokens.colors.background.default,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: tokens.radius.sm,
                                bgcolor: tokens.colors.primary[50],
                                color: tokens.colors.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <School size={16} />
                            </Box>
                            <Box sx={{ overflow: 'hidden' }}>
                              <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                                {mod.display_name || mod.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {mod.name}
                              </Typography>
                            </Box>
                          </Box>
                          {mod.menus && mod.menus.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                              {mod.menus.map((m) => (
                                <Chip
                                  key={m.id}
                                  label={m.display_name}
                                  size="small"
                                  sx={{
                                    fontSize: '0.68rem',
                                    height: 20,
                                    bgcolor: tokens.colors.grey[100],
                                    color: tokens.colors.text.primary,
                                    borderRadius: tokens.radius.sm,
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active modules assigned to this school.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>


    </Box>
  );
}

